import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionStore } from '@features/auth/store/useSessionStore';
import DashboardTabs from '../components/shared/DashboardTabs';
import KpiStrip from '../components/kpi/KpiStrip';
import CallsOverTimeChart from '../components/charts/CallsOverTimeChart';
import DispositionPieChart from '../components/charts/DispositionPieChart';
import SdrLeaderboard from '../components/charts/SdrLeaderboard';
import PipelineFunnelChart from '../components/charts/PipelineFunnelChart';
import RecentSessionsTable from '../components/activity/RecentSessionsTable';
import FilterBar from '../components/FilterBar';
import useDashboardOverviewVM from '../hooks/useDashboardOverviewVM';
import useDashboardApi from '../hooks/useDashboardApi';
import { useDashboardStore } from '../store/useDashboardStore';
import type { OrganizationInfo, SdrInfo } from '../types/dashboard.types';

export default function DashboardOverviewPage (): React.JSX.Element {
  const navigate = useNavigate();
  const { currentUser } = useSessionStore();
  const dashboardApi = useDashboardApi();
  const isSuperAdmin = currentUser?.isSuperAdmin ?? false;

  const {
    overview,
    recentSessions,
    loadingState,
    granularity,
    prevPeriod,
    setGranularity,
  } = useDashboardOverviewVM();

  // Load organizations and SDRs for the filter bar
  const [organizations, setOrganizations] = useState<OrganizationInfo[]>([]);
  const [sdrs, setSdrs] = useState<SdrInfo[]>([]);

  useEffect(() => {
    if (!isSuperAdmin) {
      return;
    }

    const loadOrgs = async (): Promise<void> => {
      const response = await dashboardApi.getOrganizationsAsync();
      if (response.success && response.data !== undefined) {
        setOrganizations(response.data);

        // Default to "Tendril" org if no org is selected
        const { filters, setFilters } = useDashboardStore.getState();
        if ((filters.orgIds?.length ?? 0) === 0) {
          const tendrilOrg = response.data.find(
            (org) => org.name.toLowerCase().includes('tendril'),
          );
          if (tendrilOrg !== undefined && currentUser?.isTendrilUser === true) {
            setFilters({ orgIds: [tendrilOrg.id] });
          }
        }
      }
    };

    void loadOrgs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  useEffect(() => {
    if (currentUser === null) {
      return;
    }

    const loadSdrs = async (): Promise<void> => {
      const response = await dashboardApi.getSdrsAsync(
        currentUser.userId,
        null,
        currentUser.organizationId,
      );
      if (response.success && response.data !== undefined) {
        setSdrs(response.data);
      }
    };

    void loadSdrs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.userId, currentUser?.organizationId]);

  const isLoading = loadingState === 'LOADING' || loadingState === 'IDLE' || overview === null;

  const handleViewAllSessions = (): void => {
    navigate('/dashboard');
  };

  const handleSessionClick = (sessionId: string): void => {
    navigate(`/dashboard/session/${sessionId}`);
  };

  return (
    <div className="space-y-4">
      {/* Header: Tabs */}
      <DashboardTabs />

      {/* Filters */}
      <FilterBar isSuperAdmin={isSuperAdmin} organizations={organizations} sdrs={sdrs} />

      {/* KPI Strip */}
      <KpiStrip kpis={overview?.kpis ?? null} isLoading={isLoading} prevPeriod={prevPeriod} />

      {/* Charts Row 1: Calls Over Time + Disposition Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <CallsOverTimeChart
          data={overview?.callsOverTime ?? []}
          granularity={granularity}
          onGranularityChange={setGranularity}
          isLoading={isLoading}
        />
        <DispositionPieChart
          data={overview?.dispositionBreakdown ?? []}
          isLoading={isLoading}
        />
      </div>

      {/* Charts Row 2: SDR Leaderboard + Pipeline Funnel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SdrLeaderboard
          data={overview?.sdrLeaderboard ?? []}
          isLoading={isLoading}
        />
        <PipelineFunnelChart
          data={overview?.pipelineFunnel ?? []}
          isLoading={isLoading}
        />
      </div>

      {/* Recent Sessions */}
      <RecentSessionsTable
        sessions={recentSessions}
        isLoading={isLoading}
        onViewAll={handleViewAllSessions}
        onSessionClick={handleSessionClick}
      />
    </div>
  );
}
